import { Box, Divider, Tab, Tabs, styled } from '@mui/material';
import { useState } from 'react';
import {
  MapPinIcon,
  CurrencyDollarIcon,
  ReceiptIcon,
} from '@phosphor-icons/react';
import theme from '../../theme';
import BillingDetails from './BillingDetails';
import PricingConfiguration from './PricingConfiguration';
import Invoices from './Invoices';
import { useBillingForm } from '../../hooks/useBillingForm';

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '16px',
    color: 'text.secondary',
    minHeight: '48px',
    '&.Mui-selected': {
      color: theme.palette.custom.textBlue,
      fontWeight: 500,
      backgroundColor: theme.palette.custom.sidebarActiveBg,
      borderRadius: '6px',
    },
  },
});

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return value === index ? <>{children}</> : null;
};

const BillingTabs = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const billingFormData = useBillingForm();

  // Handle Next button from Billing Details - save and navigate
  const handleNext = async () => {
    const success = await billingFormData.handleBillingSubmit();
    if (success) {
      // Navigate to Pricing Configuration tab
      setSelectedTab(1);
    }
  };

  const tabItems = [
    {
      label: 'Billing Details',
      icon: <MapPinIcon size={20} weight="duotone" />,
      component: (
        <BillingDetails
          billingDetails={billingFormData.billingDetails}
          billingErrors={billingFormData.billingErrors}
          updateBillingDetails={billingFormData.updateBillingDetails}
          updateBillingAddress={billingFormData.updateBillingAddress}
          setBillingErrors={billingFormData.setBillingErrors}
          loading={billingFormData.loading}
          onNext={handleNext}
          saving={billingFormData.saving}
        />
      ),
    },
    {
      label: 'Pricing Configuration',
      icon: <CurrencyDollarIcon size={20} weight="duotone" />,
      component: (
        <PricingConfiguration
          {...billingFormData}
          billingDetails={billingFormData.billingDetails}
        />
      ),
    },
    {
      label: 'Invoices',
      icon: <ReceiptIcon size={20} weight="duotone" />,
      component: <Invoices />,
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#FFF',
        borderRadius: '8px',
        border: '1px solid #DCE0E7',
      }}
    >
      <Box sx={{ padding: '10px 16px' }}>
        <StyledTabs value={selectedTab} onChange={handleTabChange}>
          {tabItems.map((tab, i) => (
            <Tab
              key={i}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
            />
          ))}
        </StyledTabs>
      </Box>
      <Divider />
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {tabItems.map((tab, index) => (
          <TabPanel key={index} value={selectedTab} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default BillingTabs;
